import { useQueries, useQuery } from '@tanstack/react-query'

import { axiosInstance } from '../../../helpers'
import {
    LeasingSeasonDetail,
    LeasingSeasonListItem,
} from '../types/leasingSeason.types'
import {
    SEASON_DETAIL_STALE_MS,
    seasonDetailKey,
    seasonsListKey,
} from './leasingSeasonKeys'

export function useSeasonIdFromQuery(search: string): number | undefined {
    const params = new URLSearchParams(search)
    const raw = params.get('seasonId')
    if (!raw) return undefined
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : undefined
}

export function useLeasingSeasonsList(
    apartmentId: number | undefined,
    options?: { includeArchived?: boolean },
) {
    const includeArchived = options?.includeArchived ?? false
    return useQuery({
        queryKey: seasonsListKey(apartmentId, includeArchived),
        queryFn: async () => {
            const { data } = await axiosInstance.get<LeasingSeasonListItem[]>(
                '/outreach/leasing-seasons/',
                {
                    params: includeArchived
                        ? { include_archived: true }
                        : undefined,
                },
            )
            return data
        },
        enabled: !!apartmentId,
        staleTime: SEASON_DETAIL_STALE_MS,
        refetchOnWindowFocus: false,
    })
}

/** Canonical season detail query — always stores unwrapped LeasingSeasonDetail. */
export function useLeasingSeasonDetail(seasonId: number | null | undefined) {
    return useQuery({
        queryKey: seasonDetailKey(seasonId),
        queryFn: async () => {
            const { data } = await axiosInstance.get<LeasingSeasonDetail>(
                `/outreach/leasing-seasons/${seasonId}/`,
            )
            return data
        },
        enabled: seasonId != null,
        staleTime: SEASON_DETAIL_STALE_MS,
        refetchOnWindowFocus: false,
    })
}

/** @deprecated Use useLeasingSeasonDetail */
export const useSeasonDetail = useLeasingSeasonDetail

export function useAllSeasonsDetails(seasonIds: number[]) {
    return useQueries({
        queries: seasonIds.map((seasonId) => ({
            queryKey: seasonDetailKey(seasonId),
            queryFn: async () => {
                const { data } = await axiosInstance.get<LeasingSeasonDetail>(
                    `/outreach/leasing-seasons/${seasonId}/`,
                )
                return data
            },
            enabled: seasonId != null,
            staleTime: SEASON_DETAIL_STALE_MS,
            refetchOnWindowFocus: false,
        })),
    })
}
